import { untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { EnumHelper } from 'mbs-ui-kit';
import { AzureAccessTierType } from 'mbs-ui-shared@models/StorageType.enum';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

export abstract class ScheduleStepBase<T extends { valid?: boolean }> extends StepBase<T> {
  public isSimpleDisabled = false;
  public invalidFFIStorageReason: { other?: boolean; storageClass?: boolean; azureAccessTierType?: boolean; syntheticFull?: boolean } = {};
  public get simpleDisabledTooltip(): string {
    if (this.invalidFFIStorageReason.other) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIStorageTooltip', { storage: this.invalidFFIStorage });
    }

    if (this.invalidFFIStorageReason.storageClass) {
      const storageClasses = StepsHelpers.getStorageClasses(true);

      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIClassTooltip', {
        class: storageClasses.find((c) => c.value === this.storage.storageClass)?.label
      });
    }

    if (this.invalidFFIStorageReason.azureAccessTierType) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIClassTooltip', {
        class: EnumHelper.EnumToSelectIndexesArray(AzureAccessTierType).find((a) => a.value === this.storage.azureAccessTierType)?.label
      });
    }

    if (this.invalidFFIStorageReason.syntheticFull) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFISyntheticTooltip');
    }

    return '';
  }

  public get hasFFIProblem(): boolean {
    return Object.keys(this.invalidFFIStorageReason).some((key) => this.invalidFFIStorageReason[key]);
  }

  protected invalidFFIStorage: string;

  private storage: Record<string, unknown> = {};

  protected constructor(
    public mainService: RemoteManagementWizardsService,
    protected stepService: WizardStepsService,
    protected i18nextService: I18NextService
  ) {
    super(mainService);
  }

  initForm(): void {
    this.isSimpleDisabled = !this.isFFIEnabled;
  }

  protected initEvents(): void {
    this.stepService
      .pipeProperty('storage')
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.storage = data;
        this.invalidFFIStorage = data.invalidFFIStorage;
        this.invalidFFIStorageReason = { syntheticFull: this.invalidFFIStorageReason.syntheticFull };

        if (data.invalidFFIStorage) {
          this.isSimpleDisabled = true;
          this.invalidFFIStorageReason = { other: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };

          this.deactivateSimple();

          if (StepsHelpers.isStorageBadForFFI(data.storageClass, 'Amazon')) {
            this.invalidFFIStorageReason = { storageClass: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };
          }

          if (StepsHelpers.isStorageBadForFFI(data.azureAccessTierType, 'Azure')) {
            this.invalidFFIStorageReason = { azureAccessTierType: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };
          }

          return;
        }

        this.isSimpleDisabled = !this.isFFIEnabled || this.hasFFIProblem;
      });

    this.stepService
      .pipeProperty('options', 'syntheticFull')
      .pipe(untilDestroyed(this))
      .subscribe((syntheticFull) => {
        this.invalidFFIStorageReason.syntheticFull = !syntheticFull;

        this.isSimpleDisabled = !this.isFFIEnabled || this.hasFFIProblem;
      });
  }

  protected abstract deactivateSimple(): void;
}
